// Del Approval Table

import { useState } from "react";
import styles from "./table-common.module.css";
import { ContextMenu } from "../../../common/ContextMenu";
import { buildCanonicalHomePath } from "../../../../services/RouteUtils";
import { useTableSort } from "./useTableSort";
import { SortableHeader } from "./SortableHeader";
import { utcToLocalDateString } from "../../../../utils/dateUtils";

// Del Approval Table
export const DelApprovalTable = ({
  dels,
  onRowSelect,
  onDoubleClick,
  selectedDelId,
}) => {
  const [contextMenu, setContextMenu] = useState(null);
  const { sortedItems, sortColumn, sortDirection, onSort } = useTableSort(dels);

  const handleContextMenu = (e, del) => {
    e.preventDefault();
    onRowSelect(del);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      del: del,
    });
  };

  const getDelUrl = (del) => {
    return buildCanonicalHomePath({
      tab: "approvals",
      delId: del.id,
      action: "approve",
      open: true,
    });
  };

  const handleOpenInNewTab = (del) => {
    const url = getDelUrl(del);
    window.open(window.location.origin + url, "_blank");
  };

  const handleCopyLink = (del) => {
    const url = window.location.origin + getDelUrl(del);
    navigator.clipboard.writeText(url).then(() => {
      console.log("Link copied to clipboard");
    });
  };

  const getContextMenuOptions = (del) => [
    {
      label: "Open in new tab",
      action: () => handleOpenInNewTab(del),
    },
    {
      label: "Copy link",
      action: () => handleCopyLink(del),
    },
  ];
  return (
    <div className={styles.delTableContainer} id="approval_table">
      {/* Card View (Mobile & Tablet) */}
      <div className={styles.cardContainer}>
        <div className={styles.cardList}>
          {dels.map((del) => (
            <div
              key={`card-${del.id}`}
              onClick={() => onRowSelect(del)}
              onDoubleClick={() => onDoubleClick(del)}
              onContextMenu={(e) => handleContextMenu(e, del)}
              className={`${styles.card} ${
                del.id === selectedDelId ? styles.selectedCard : ""
              }`}
            >
              <div className={styles.cardContent}>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Creation Date:</span>
                  <span className={styles.value}>{del.del_creation_date}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Updated Date:</span>
                  <span className={styles.value}>
                    {utcToLocalDateString(del.updated)}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Submitted Date:</span>
                  <span className={styles.value}>{del.del_submit_date}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>DEL Date:</span>
                  <span className={styles.value}>{del.del_date}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Client Name:</span>
                  <span className={styles.value}>{del.client_name}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Project Number:</span>
                  <span className={styles.value}>
                    {del.project_number !== "-"
                      ? del.project_number
                      : del.project_name_description}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Project Name:</span>
                  <span className={styles.value}>{del.project_name}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Phase ID:</span>
                  <span className={styles.value}>{del.phase_id}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Task ID:</span>
                  <span className={styles.value}>{del.task_id}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Project Manager:</span>
                  <span className={styles.value}>
                    {del.project_manager_name}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Invoice Manager:</span>
                  <span className={styles.value}>
                    {del.invoice_manager_name}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Submitted By:</span>
                  <span className={styles.value}>{del.username}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Table View (Desktop) */}
      <table className={`${styles.table} ${styles.approvalTable}`}>
        <thead>
          <tr>
            <SortableHeader label="Creation Date" column="del_creation_date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader label="Updated Date" column="updated" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader label="Submitted Date" column="del_submit_date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader label="DEL Date" column="del_date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <th>Client Name</th>
            <th>Project Number</th>
            <th>Project Name</th>
            <th>Phase ID</th>
            <th>Task ID</th>
            <th>Project Manager</th>
            <th>Invoice Manager</th>
            <th>Submitted By</th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((del) => (
            <tr
              key={`row-${del.id}`}
              onClick={() => onRowSelect(del)}
              onDoubleClick={() => onDoubleClick(del)}
              onContextMenu={(e) => handleContextMenu(e, del)}
              className={del.id === selectedDelId ? styles.selectedRow : ""}
            >
              <td title={del.del_creation_date}>{del.del_creation_date}</td>
              <td title={utcToLocalDateString(del.updated)}>
                {utcToLocalDateString(del.updated)}
              </td>
              <td title={del.del_submit_date}>{del.del_submit_date}</td>
              <td title={del.del_date}>{del.del_date}</td>
              <td title={del.client_name}>{del.client_name}</td>
              <td
                title={
                  del.project_number !== "-"
                    ? del.project_number
                    : del.project_name_description
                }
              >
                {del.project_number !== "-"
                  ? del.project_number
                  : del.project_name_description}
              </td>
              <td title={del.project_name}>{del.project_name}</td>
              <td title={del.phase_id}>{del.phase_id}</td>
              <td title={del.task_id}>{del.task_id}</td>
              <td title={del.project_manager_name}>
                {del.project_manager_name}
              </td>
              <td title={del.invoice_manager_name}>
                {del.invoice_manager_name}
              </td>
              <td title={del.username}>{del.username}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          options={getContextMenuOptions(contextMenu.del)}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};
